
	import Vue from 'vue';
	import Component from 'vue-class-component';

	import HeaderMenu from './HeaderMenu.vue';
	import appStateUpdator from '@/store/AppStateUpdator';
	import {
		NavigationConfiguration,
		NavigationTab,
		NextRoute,
		Project,
		RouteConfig
	} from '@/types';
	import { mdiHelpCircleOutline, mdiHome } from '@mdi/js';
	import { fetchProject } from '@/services/api/projects';
	import navigationService from '@/services/api/navigation';
	import { NavigationManager } from '@/services/Navigation/NavigationManager';
	import projectCountInfoService from '@/services/Navigation/ProjectCountInfoService';

	const Props = Vue.extend({
		props: {
			isLogged: {
				type: Boolean,
				default: false
			}
		}
	});

	/** The Header of the application */
	@Component({
		watch: {
			$route: {
				handler(to, from) {
					// if refreshed
					if (to.name !== null) {
						(this as any).initCurrentRouteParams();
					}

					const toToken = to?.params.projectToken;
					const fromToken = from?.params.projectToken;

					// if not a projectroute
					if (!toToken) {
						(this as any).project = {} as Project;

						return;
					}

					// if project need to be updated
					if (toToken && (!(this as any).project || !fromToken || fromToken !== toToken)) {
						(this as any).fetchProject(toToken);
					}
				},
				// Fired when mounted
				immediate: true,
				deep: true
			}
		},
		components: {
			HeaderMenu
		},
		inheritAttrs: false
	})
	export default class Header extends Props {
		tab = '';
		project = {} as Project;
		navigationConfiguration = {} as NavigationConfiguration;
		navigationManager = {} as NavigationManager;
		homeIcon = mdiHome;
		helpIcon = mdiHelpCircleOutline;
		supportLinkUrl = '';
		supportLinkLabel = '';

		get hasSupportLink() {
			return this.supportLinkUrl !== '';
		}

		get routes(): RouteConfig[] {
			return this.$router.options.routes as RouteConfig[];
		}

		get isProjectRoute(): boolean {
			return !!(this.$route.params && this.$route.params.projectToken);
		}

		get hasMoreThanOneProject(): boolean | null | 0 {
			const projectCount = projectCountInfoService.getProjectCountStore();

			return null === projectCount ? true : projectCount > 1;
		}

		get tabs(): NavigationTab[] {
			const tabs = [] as NavigationTab[];
			// we create the navigationTab from the route if label is defined and roles ok
			this.routes?.forEach((route: RouteConfig) => {
				if (!route.meta) {
					return;
				}

				route.to = this.createNextRouteTo(route);

				if (!this.validateRoleForRoute(route) || !this.validateConfigurationForRoute(route)) {
					return;
				}

				const label: string = this.navigationManager.getLabel(route, this.navigationConfiguration);
				const subLabel: string = this.navigationManager.getSubLabel(route, this.navigationConfiguration);
				const orderProperty: number = this.navigationManager.getOrderProperty(route, this.navigationConfiguration);
				const supportLinkUrl: string = this.navigationManager.getSupportLink(route, this.navigationConfiguration) ?? '';
				const supportLinkLabel: string = this.navigationManager.getSupportLinkLabel(route, this.navigationConfiguration) ?? '';
				const routeName: string = route.name ?? '';

				if (undefined !== label) {
					tabs.push({ label, subLabel, orderProperty, supportLinkUrl, supportLinkLabel, routeName, to: route.to });
				}
			});

			return tabs;
		}

		get mainTabs(): NavigationTab[] {
			// 1. Get all the main tabs labels
			const labels = this.tabs.map((tab) => tab.label);
			const uniquesLabels = [...new Set(labels)];

			// 2. Get all the main tabs
			const mainTabs = [] as NavigationTab[];

			uniquesLabels.forEach((label) => {
				// the first subtab selected is the first route in the router file
				const singleTab = this.tabs.find((tab) => tab.label === label);

				if (undefined !== singleTab) {
					mainTabs.push(singleTab);
				}
			});

			return this.orderMainTabs(mainTabs);
		}

		get subTabs(): NavigationTab[] {
			const currentRouteName = this.$route.name;
			const currentTab = this.tabs.find((tab) => tab.routeName === currentRouteName);

			if (undefined === currentTab) {
				return [];
			}

			this.supportLinkUrl = currentTab.supportLinkUrl ?? '';
			this.supportLinkLabel = currentTab.supportLinkLabel ?? '';

			const allSubTabs = this.tabs.filter((tab) => {
				return tab.subLabel;
			});

			const subTabs = allSubTabs.filter((tab) => tab.label === currentTab.label);

			if (subTabs.length < 2) {
				return [];
			}

			return subTabs;
		}

		created() {
			this.navigationManager = new NavigationManager();
		}

		orderMainTabs(mainTabs: NavigationTab[]): NavigationTab[] {
			mainTabs.sort((a, b) => {
				const priorityRoutes = ['projects', 'project'];

				if (false === priorityRoutes.includes(a.to.name) && true === priorityRoutes.includes(b.to.name)) {
					return 1;
				}

				if (true === priorityRoutes.includes(a.to.name) && false === priorityRoutes.includes(b.to.name)) {
					return -1;
				}

				if (a.orderProperty > b.orderProperty) {
					return 1;
				}

				return -1;
			});

			return mainTabs;
		}

		createNextRouteTo(route: RouteConfig): NextRoute {
			const to = { name: route.name as string, params: {} };
			if (!this.isProjectRoute) {
				if (route.meta?.params) {
					Object.assign(to.params, route.meta?.params);
				}

				return to;
			}

			if ('actors' === route.name) {
				to.params = { projectToken: this.$route.params.projectToken, actorsRole: 'experimentateur-primaire' };

				return to;
			}

			to.params = { projectToken: this.$route.params.projectToken };

			if (route.meta?.params) {
				Object.assign(to.params, route.meta?.params);
			}

			return to;
		}

		validateRoleForRoute(route: RouteConfig): boolean {
			// if no roles is needed
			if (!route.meta?.can) {
				return true;
			}

			// if it's an accessmaster roles
			if ('string' === typeof route.meta.can) {
				return this.$can(route.meta.can);
			}

			// if it's not a project route, all cases should already be handled
			if (!this.isProjectRoute) {
				return false;
			}

			// if it's a project route
			return route.meta.can(route.to, undefined, this.$can);
		}

		validateConfigurationForRoute(route: RouteConfig): boolean {
			// if it's a project route and the option hiddenOnProjectRoute is true
			if (this.isProjectRoute && route.meta && route.meta.hiddenOnProjectRoute) {
				return false;
			}

			return !(this.navigationManager.hasMandatoryConfiguration(route)
				&& !this.navigationManager.hasConfigurationForRoute(route, this.navigationConfiguration));
		}

		fetchProject(projectToken: string) {
			fetchProject(projectToken)
				.then((response) => this.project = response.data);
			navigationService.getByProject(projectToken)
				.then((response) => {
					this.navigationConfiguration = response.data;
				});
		}

		initCurrentRouteParams() {
			this.routes?.forEach((route: RouteConfig) => {
				if (route.to?.name !== this.$route.name) {
					return;
				}

				if (route.meta?.params) {
					Object.assign(this.$route.params, route.meta.params);
				}
			});
		}

		redirectSupport() {
			window.open(`${this.supportLinkUrl}`, '_blank', 'noopener noreferrer');
		}

		get loaded(): boolean {
			return appStateUpdator.isLoaded();
		}
	}
